import React, { useLayoutEffect, useRef, useEffect, useState } from "react";
import { MinusCircleIcon, CheckCircleIcon, UserGroupIcon, ClockIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

import AddAppointment from "./AddAppointment";
import DefineSlotOrder from "./DefineSlotOrder";

import Calendar from "./Calendar";
import {
  fetchRentedApartmentsListingsData,
  DeletedListingsAndInfo,
  updateAutomaticEmail,
  ListApartment,
} from "./DataServices/ApartmentsListingsDataServices";
import {
  saveAppointments,
  fetchAppointmentsPerListingData,
} from "./DataServices/AppointmentsDataServices";
import AddRentedListing from "./AddRentedListing";
import MoveOutReminderModal from "./MoveOutReminderModal";
import SecurityDepositReceiverModal from "./SecurityDepositReceiverModal";
import { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import {
  EllipsisVerticalIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";
import MessageBox from "./MessageBox";
import Notification from "./Notification";
import PencilIcon from "@heroicons/react/24/outline/PencilIcon";
import PauseIcon from "@heroicons/react/24/outline/PauseIcon";
import PlayIcon from "@heroicons/react/24/outline/PlayIcon";
import EnvelopeIcon from "@heroicons/react/24/outline/EnvelopeIcon";
import EmailIcon from "@mui/icons-material/Email";
import BedIcon from "@mui/icons-material/Bed";
import ApartmentIcon from "@mui/icons-material/Apartment";
import RoomIcon from "@mui/icons-material/Room";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import {
  CalendarDaysIcon,
  CheckBadgeIcon,
  PlusIcon,
  TrashIcon,
  BellIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import Loading from "./Loading";
import { PauseCircle, PlayCircle } from "@mui/icons-material";
import { IAvailableAppointment, Reminder } from "./Models";
import {
  sendSecurityDepositReceiverEmail,
  sendSlotBookingEmail,
} from "./DataServices/SecurityDepositDataServices";
import { sendGeneralInstructionEmail } from "./DataServices/EmailService";
import { baseUrl } from "./DataServices/Configuration";
import { getTokenFromLocalStorage } from "./DataServices/Configuration";
import ArrangeShowingsPlanner from "./ArrangeShowingsPlanner";

export interface IListing {
  id: string;
  idApartment: string;
  title: string;
  zillowTitle: string;
  description: string;
  rent: string;
  moveOutDate: string;
  moveInDate: string;
  showings: string;
  areAvailableAppointmentsSet: boolean;
  isSelected: boolean;
  isVacant: boolean;
  building: string;
  line: string;
  floor: number;
  numOfBedrooms: number;
  currentRent: number;
  automaticEmail: boolean;
  accessCode: string;
  appfolioLink: string;
  youTubeLink: string;
  workflowType?: number;
}

export interface ITenantContact {
  id: string;
  listingId: string;
  name: string;
  email: string;
  phone: string;
  isGuarantor: boolean;
  isReceiver: boolean;
  showingAppointmentStarted: boolean;
  lastSelectedReceiverId?: string | null;
  // createdAt, isDeleted exist in DB but not needed for UI right now
}

export default function RentedListing() {
  const navigate = useNavigate();
  function classNames(...classes: any[]) {
    return classes.filter(Boolean).join(" ");
  }

  const [
    showCalendarConfirmedAppointments,
    setShowCalendarConfirmedAppointments,
  ] = useState(true);
  const [showAddListing, setShowAddListing] = useState(false);
  const [showDefineSlotOrder, setShowDefineSlotOrder] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [availableAppointments, setAvailableAppointments] = useState<
    IAvailableAppointment[]
  >([]);
  const [emptyAppointments, setEmptyAppointments] = useState<
    IAvailableAppointment[]
  >([]);
  const [availableAppointmentsPerListing, setAvailableAppointmentsPerListing] =
    useState<IAvailableAppointment[]>([]);

  const emptyAppointment: IAvailableAppointment = {
    id: "",
    listingID: "",
    startTime: null,
    endTime: null,
    capacity: 0,
    slotOrder: 0,
    groupId: "",
    reminders: [],
  };

  const handleAutomaticEmail = async (apartmentForAppointment: IListing) => {
    try {
      await updateAutomaticEmail(apartmentForAppointment.idApartment);
      setRefreshApartments(true);
    } catch (error) {
      console.log("Error in handleAutomaticEmail:", error);
    }
  };
  const handleListApartment = async (apartment: IListing) => {
    try {
      await ListApartment(apartment.id);

      setNotificationMessage("Apartment listed successfully!");
      setNotificationMessageType("success");
      setOpenNotification(true);

      setTimeout(() => setOpenNotification(false), 3000);

      // refresh list
      setRefreshApartments(true);
    } catch (error) {
      console.error("Error listing apartment:", error);
      setNotificationMessage("Failed to list apartment!");
      setNotificationMessageType("error");
      setOpenNotification(true);
    }
  };

  const handleMoveOutReminder = (apartment: IListing) => {
    setSelectedListingForReminder(apartment);
    setShowMoveOutReminderModal(true);
  };

  const handleMakeMoveOut = async (apartment: IListing) => {
    try {
      const baseUrlOrigin = window.location.origin;
      const data = await sendSecurityDepositReceiverEmail(
        apartment.id,
        baseUrlOrigin,
      );

      setNotificationMessage(
        data.message ||
          "Security deposit receiver selection emails sent successfully!",
      );
      setNotificationMessageType("success");
      setOpenNotification(true);
    } catch (error: any) {
      console.error("Error sending security deposit emails:", error);
      setNotificationMessage(
        error.message ||
          "Failed to send emails. Please check your connection and try again.",
      );
      setNotificationMessageType("error");
      setOpenNotification(true);
    }
  };
  useEffect(() => {}, [availableAppointments]);
  const handleShowAvailableAppointments = (
    apartmentForAppointment: IListing,
  ) => {
    fetchAppointmentsPerListingData(apartmentForAppointment.id)
      .then((appointments) => {
        const now = new Date();
        const newAppointments = appointments.filter(
          (appointment: any) =>
            appointment.endTime && new Date(appointment.endTime) > now,
        );

        setAvailableAppointmentsPerListing(newAppointments);
      })
      .catch((error: any) => {
        console.error("Error fetching appointments:", error);
      });

    setShowCalendarConfirmedAppointments(false);
    setEmptyListings([apartmentForAppointment]);
    setShowCalendar(true);
  };
  const handleSendSlotBookingMail = async (apartment: IListing) => {
    try {
      const baseUrlOrigin = window.location.origin;
      const data = await sendSlotBookingEmail(apartment.id, baseUrlOrigin);

      setNotificationMessage(
        data.message || "Slot booking emails sent successfully!",
      );
      setNotificationMessageType("success");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 3000);
    } catch (error: any) {
      console.error("Error sending slot booking emails:", error);
      setNotificationMessage(
        error.message ||
          "Failed to send emails. Please check your connection and try again.",
      );
      setNotificationMessageType("error");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 3000);
    }
  };

  const handleSendGeneralInstructionEmail = async (apartment: IListing) => {
    try {
      const data = await sendGeneralInstructionEmail(apartment.id);

      setNotificationMessage(
        data.message || "General instruction emails sent successfully!",
      );
      setNotificationMessageType("success");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 3000);
    } catch (error: any) {
      console.error("Error sending general instruction emails:", error);
      setNotificationMessage(
        error.message ||
          "Failed to send emails. Please check your connection and try again.",
      );
      setNotificationMessageType("error");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 3000);
    }
  };
  const handleAddAppointment = (listingForAppointment: IListing) => {
    setSelectedListingsToAddAppointment(listingForAppointment);
    setOpenAddAppointment(true);
  };
  const checkbox = useRef<HTMLInputElement>(null);
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [selectedListings, setSelectedListings] = useState<IListing[]>([]);
  const [selectedListingsToBeDeleted, setSelectedListingsToBeDeleted] =
    useState<IListing[]>([]);
  const [
    selectedListingsToAddAppointment,
    setSelectedListingsToAddAppointment,
  ] = useState<IListing>();
  const [openMessageBox, setOpenMessageBox] = useState(false);
  const [openMessageBox2, setOpenMessageBox2] = useState(false);
  const [openNotification, setOpenNotification] = useState(false);
  const [openAddAppointment, setOpenAddAppointment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [listings, setListings] = useState<IListing[]>([]);
  const [emptyListings, setEmptyListings] = useState<IListing[]>([]);
  const [refreshApartments, setRefreshApartments] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [notificationMessageType, setNotificationMessageType] =
    useState("success");
  const [openAppointments, setOpenAppointments] = useState<
    IAvailableAppointment[]
  >([]);
  const [showMoveOutReminderModal, setShowMoveOutReminderModal] =
    useState(false);
  const [selectedListingForReminder, setSelectedListingForReminder] =
    useState<IListing | null>(null);

  // Mobile menu state
  const [mobileMenuApartment, setMobileMenuApartment] =
    useState<IListing | null>(null);

  useEffect(() => {}, []);

  // NEW: state to toggle Listed vs Unlisted view
  const [listed, setListed] = useState<boolean>(true);

  // 🔹 Tenant management state
  const [openTenantsModal, setOpenTenantsModal] = useState(false);
  const [selectedListingForTenants, setSelectedListingForTenants] =
    useState<IListing | null>(null);
  const [tenants, setTenants] = useState<ITenantContact[]>([]);
  const [tenantLoading, setTenantLoading] = useState(false);
  const [tenantError, setTenantError] = useState<string | null>(null);
  const [showTenantForm, setShowTenantForm] = useState(false);
  const [isEditingTenant, setIsEditingTenant] = useState(false);
  const [tenantForm, setTenantForm] = useState<{
    id: string | null;
    name: string;
    email: string;
    phone: string;
    isGuarantor: boolean;
    isReceiver: boolean;
    showingAppointmentStarted: boolean;
  }>({
    id: null,
    name: "",
    email: "",
    phone: "",
    isGuarantor: false,
    isReceiver: false,
    showingAppointmentStarted: false,
  });

  // Tenant delete confirmation state
  const [tenantToDelete, setTenantToDelete] = useState<ITenantContact | null>(
    null,
  );
  const [openDeleteTenantConfirm, setOpenDeleteTenantConfirm] = useState(false);

  // 🔹 Security Deposit Receiver Selection Modal
  const [openReceiverModal, setOpenReceiverModal] = useState(false);

  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showArrangeShowings, setShowArrangeShowings] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // initial fetch — now depends on listed so it fetches correct set
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        // pass `listed` so service can filter on server side if supported
        const data = await fetchRentedApartmentsListingsData(listed);
        const listingsWithMoveInDate = data.map((listing: any) => ({
          ...listing,
          moveInDate: listing.moveInDate ?? "",
        }));
        setListings(listingsWithMoveInDate);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Error fetching data:", error);
      }
    }
    fetchData();
  }, [openAddAppointment, listed]); // run when adding appointment modal changes or listed toggles

  useLayoutEffect(() => {
    const isIndeterminate =
      selectedListings.length > 0 && selectedListings.length < listings.length;
    setChecked(
      selectedListings.length === listings.length &&
        selectedListings.length > 0,
    );
    setIndeterminate(isIndeterminate);
    if (checkbox.current !== null) {
      checkbox.current.indeterminate = isIndeterminate;
    }
  }, [selectedListings, listings.length]);

  const handleApartmentSelection = (apartment: IListing) => {
    if (selectedListings.includes(apartment)) {
      setSelectedListings(selectedListings.filter((a) => a !== apartment));
    } else {
      setSelectedListings([...selectedListings, apartment]);
    }
  };

  const handleAddApartment = () => {
    setSelectedListings([]);
    setShowAddListing(true);
  };

  const handleDeleteApartment = async (apartment: IListing) => {
    setSelectedListingsToBeDeleted([]);
    setSelectedListingsToBeDeleted([apartment]);
    const appointments = await fetchAppointmentsPerListingData(apartment.id);
    setOpenAppointments(appointments);
    setOpenMessageBox(true);
  };

  const handleDeleteApartments = () => {
    setSelectedListingsToBeDeleted([]);
    setSelectedListingsToBeDeleted(selectedListings);
    setOpenMessageBox(true);
  };

  const handleAddEditListing = (apt: IListing) => {
    const updatedListings = listings.map((listing) => ({
      ...listing,
      isSelected: false,
    }));
    setListings(updatedListings);

    setSelectedListings([]);
    setSelectedListings([apt]);
    setShowAddListing(true);
  };

  // 🔹 Tenants: API helpers
  const fetchTenantsForListing = async (listingId: string) => {
    try {
      setTenantLoading(true);
      setTenantError(null);
      const token = await getTokenFromLocalStorage();
      const url = `${baseUrl}/api/Appointments/TenantContacts/Listing/${listingId}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP error ${response.status}`);
      }

      const data = (await response.json()) as ITenantContact[];
      setTenants(data);
    } catch (err: any) {
      console.error("Error fetching tenants:", err);
      setTenantError(err.message || "Failed to load tenants.");
      setTenants([]);
    } finally {
      setTenantLoading(false);
    }
  };

  const handleOpenTenantsModal = async (apartment: IListing) => {
    setSelectedListingForTenants(apartment);
    setOpenTenantsModal(true);
    setShowTenantForm(false);
    setIsEditingTenant(false);
    setTenantForm({
      id: null,
      name: "",
      email: "",
      phone: "",
      isGuarantor: false,
      isReceiver: false,
      showingAppointmentStarted: false,
    });
    await fetchTenantsForListing(apartment.id);
  };

  const handleCloseTenantsModal = () => {
    setOpenTenantsModal(false);
    setSelectedListingForTenants(null);
    setTenants([]);
    setShowTenantForm(false);
    setTenantError(null);
  };

  const handleStartAddTenant = () => {
    setIsEditingTenant(false);
    setTenantForm({
      id: null,
      name: "",
      email: "",
      phone: "",
      isGuarantor: false,
      isReceiver: false,
      showingAppointmentStarted: false,
    });
    setShowTenantForm(true);
  };

  const handleStartEditTenant = (tenant: ITenantContact) => {
    setIsEditingTenant(true);
    setTenantForm({
      id: tenant.id,
      name: tenant.name,
      email: tenant.email,
      phone: tenant.phone,
      isGuarantor: tenant.isGuarantor,
      isReceiver: tenant.isReceiver,
      showingAppointmentStarted: tenant.showingAppointmentStarted,
    });
    setShowTenantForm(true);
  };

  const handleDeleteTenant = async (tenant: ITenantContact) => {
    try {
      const token = await getTokenFromLocalStorage();
      const url = `${baseUrl}/api/Appointments/TenantContact/${tenant.id}`;

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP error ${response.status}`);
      }

      setTenants((prev) => prev.filter((t) => t.id !== tenant.id));
    } catch (err: any) {
      console.error("Error deleting tenant:", err);
      setTenantError(err.message || "Failed to delete tenant.");
    }
  };

  const handleConfirmDeleteTenant = async () => {
    if (!tenantToDelete) return;
    await handleDeleteTenant(tenantToDelete);
    setOpenDeleteTenantConfirm(false);
    setTenantToDelete(null);
  };

  const handleSaveTenant = async () => {
    if (!selectedListingForTenants) return;

    try {
      setTenantError(null);
      const token = await getTokenFromLocalStorage();

      const payload = {
        id: tenantForm.id ?? crypto.randomUUID(),
        listingId: selectedListingForTenants.id,
        name: tenantForm.name,
        email: tenantForm.email,
        phone: tenantForm.phone,
        isGuarantor: tenantForm.isGuarantor,
        isReceiver: tenantForm.isReceiver,
        showingAppointmentStarted: tenantForm.showingAppointmentStarted,
      };

      if (isEditingTenant && tenantForm.id) {
        // Update
        const url = `${baseUrl}/api/Appointments/TenantContact/${tenantForm.id}`;
        const response = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        const text = await response.text();
        if (!response.ok) {
          throw new Error(text || `HTTP error ${response.status}`);
        }

        // Refresh tenants list
        await fetchTenantsForListing(selectedListingForTenants.id);
      } else {
        // Add
        const url = `${baseUrl}/api/Appointments/TenantContact`;
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        const text = await response.text();
        if (!response.ok) {
          throw new Error(text || `HTTP error ${response.status}`);
        }

        // Refresh tenants list
        await fetchTenantsForListing(selectedListingForTenants.id);
      }

      setShowTenantForm(false);
      setTenantForm({
        id: null,
        name: "",
        email: "",
        phone: "",
        isGuarantor: false,
        isReceiver: false,
        showingAppointmentStarted: false,
      });
    } catch (err: any) {
      console.error("Error saving tenant:", err);
      setTenantError(err.message || "Failed to save tenant.");
    }
  };

  // 🔹 Security Deposit Receiver Selection Handlers
  const handleOpenReceiverModal = () => {
    setOpenReceiverModal(true);
  };

  const handleCloseReceiverModal = () => {
    setOpenReceiverModal(false);
  };

  const handleReceiverUpdated = async () => {
    // Refresh the tenants list after receiver is updated
    if (selectedListingForTenants) {
      await fetchTenantsForListing(selectedListingForTenants.id);
    }
  };

  //delete listing(s) after
  const okButtonFunction = async () => {
    try {
      if (openAppointments.length > 0) {
        setOpenMessageBox2(true);
      } else {
        const appointments = await DeletedListingsAndInfo(
          selectedListingsToBeDeleted,
        );

        setAvailableAppointmentsPerListing(appointments);
        setOpenNotification(true);
        setNotificationMessage("Apartment unlisted successfully!");
        setNotificationMessageType("success");
        setTimeout(() => {
          setOpenNotification(false);
        }, 3000);

        setRefreshApartments(true);
        setSelectedListingsToBeDeleted([]);
        setSelectedListings([]);
      }
    } catch (error) {
      console.error("Error deleting apartments:", error);
      setNotificationMessage("Failed to delete Apartment!");
      setNotificationMessageType("error");
      setTimeout(() => {
        setOpenNotification(false);
      }, 3000);
    } finally {
      setOpenMessageBox(false);
    }
  };
  const okButtonFunction2 = async () => {
    DeletedListingsAndInfo(selectedListingsToBeDeleted)
      .then((appointments) => {
        setOpenNotification(true);
        setAvailableAppointmentsPerListing(appointments);
        setNotificationMessage("Apartment deleted successfully!");
        setNotificationMessageType("success");
        setOpenNotification(true);
        setTimeout(() => {
          setOpenNotification(false);
        }, 3000);
        setRefreshApartments(true);
      })
      .catch((error: any) => {
        console.error("Error deleting apartment:", error);
        setNotificationMessage("Failed to delete Apartment!");
        setNotificationMessageType("error");
        setOpenNotification(true);
        setTimeout(() => {
          setOpenNotification(false);
        }, 3000);
      });
    setOpenMessageBox(false);
    setOpenMessageBox2(false);
    setSelectedListingsToBeDeleted([]);
    setSelectedListings([]);
  };

  //delete listing(s) after
  const cancelButtonFunction = () => {
    setOpenMessageBox(false);
  };

  useEffect(() => {
    if (refreshApartments) {
      const fetchData = async () => {
        try {
          setListings([]);
          const data = await fetchRentedApartmentsListingsData(listed);
          setListings(data);
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setRefreshApartments(false);
        }
      };
      fetchData();
    }
  }, [refreshApartments, setRefreshApartments, setListings, listed]);

  function toggleAll() {
    setSelectedListings(checked || indeterminate ? [] : listings);
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  }

  const handleModalClose = (): void => {
    setRefreshApartments(true);
  };

  const handleSearch = (query: string) => setSearchQuery(query);
  const filteredListings = listings.filter((apartment) =>
    apartment.title.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleShowApartmentListing = () => {
    setListed(!listed);
  };

  const handleOpenArrangeShowings = () => {
    if (!listed) {
      setListed(true);
    }
    setShowArrangeShowings(true);
  };

  return (
    <>
      <div className="mt-16 sm:mt-20">
        {showCalendar ? (
          <Calendar
            setShowCalendar={setShowCalendar}
            relatedListing={emptyListings}
            showAvailableAppointments={!showCalendarConfirmedAppointments}
            disableMainAppointmentFetch={true}
          />
        ) : showArrangeShowings ? (
          <div className="px-2 sm:px-4 lg:px-8 relative">
            <ArrangeShowingsPlanner
              listings={listings}
              onBack={() => setShowArrangeShowings(false)}
            />
          </div>
        ) : (
          <div className="px-2 sm:px-4 lg:px-8 relative">
            {/* Responsive Header */}
            <div className="flex flex-col gap-3 pb-4">
              {/* Row 1: Heading and Notification */}
              <div className="flex items-center justify-between gap-2">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight truncate">
                  {listed ? "Rented Apartments (Listed)" : "Rented Apartments (Unlisted)"}
                </h1>
                <div className="sm:hidden">
                  <Notification
                    isOpen={openNotification}
                    message={notificationMessage}
                    messageType={notificationMessageType}
                    setIsOpen={setOpenNotification}
                  />
                </div>
              </div>

              {/* Row 2: Search Bar */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="relative flex-1 sm:max-w-md">
                  <MagnifyingGlassIcon
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  <input
                    type="text"
                    name="search"
                    id="search"
                    placeholder="Search By Title..."
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-2 w-full rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition shadow-sm"
                  />
                </div>
                <div className="hidden sm:block">
                  <Notification
                    isOpen={openNotification}
                    message={notificationMessage}
                    messageType={notificationMessageType}
                    setIsOpen={setOpenNotification}
                  />
                </div>
              </div>

              {/* Row 3: Action Buttons */}
              <div className="flex flex-wrap justify-start sm:justify-end gap-2 w-full">
                  <button
                    type="button"
                    onClick={handleOpenArrangeShowings}
                    className="inline-flex items-center justify-center gap-1 sm:gap-2 py-2 px-3 sm:px-4 text-xs sm:text-sm font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 active:scale-[0.98] transition-all duration-200 w-[calc(50%-0.25rem)] sm:w-auto flex-none"
                  >
                    <CalendarDaysIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    <span className="whitespace-nowrap">Arrange Showings</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate("/extracted-apartments")}
                    className="inline-flex items-center justify-center gap-1 sm:gap-2 py-2 px-3 sm:px-4 text-xs sm:text-sm font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 active:scale-[0.98] transition-all duration-200 w-[calc(50%-0.25rem)] sm:w-auto flex-none"
                  >
                    <UserGroupIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    <span className="">Extracted Data</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleShowApartmentListing}
                    className="inline-flex items-center justify-center gap-1 sm:gap-2 py-2 px-3 sm:px-4 text-xs sm:text-sm font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 active:scale-[0.98] transition-all duration-200 w-[calc(50%-0.25rem)] sm:w-auto flex-none"
                  >
                    {listed ? (
                      <EyeSlashIcon className="h-4 w-4 hidden sm:block" />
                    ) : (
                      <EyeIcon className="h-4 w-4 hidden sm:block" />
                    )}
                    <span>Show {listed ? "Unlisted" : "Listed"}</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleAddApartment}
                    className="inline-flex items-center justify-center gap-1 sm:gap-2 py-2 px-3 sm:px-4 text-xs sm:text-sm font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 active:scale-[0.98] transition-all duration-200 w-[calc(50%-0.25rem)] sm:w-auto flex-none"
                  >
                    <PlusIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    <span className="">Rented Apartment</span>
                  </button>
                </div>
              </div>

            {/* Responsive Table Container */}
            <div className="mt-4 sm:mt-5 flow-root">
              <div className="-mx-2 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <div className="overflow-hidden">
                    {selectedListings.length > 0 && (
                      <div className="relative mb-2 sm:mb-0">
                        <div className="flex sm:absolute left-14 top-0 items-center space-x-2 sm:space-x-3 bg-gray-100 p-2 sm:p-0 sm:bg-transparent sm:left-12">
                          <button
                            type="button"
                            onClick={handleDeleteApartments}
                            className="inline-flex items-center rounded bg-gray-100 px-3 py-1.5 sm:px-2 sm:py-1 text-xs sm:text-sm font-semibold text-black shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
                          >
                            Delete Selected
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Mobile Card View */}
                    {isMobile ? (
                      <div className="space-y-3">
                        {filteredListings.length > 0 &&
                          filteredListings.map((apartment) => (
                            <div
                              key={apartment.id}
                              className={`border rounded-lg p-4 shadow-sm ${
                                selectedListings.includes(apartment)
                                  ? "bg-gray-50 border-blue-200"
                                  : "bg-white"
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                  <input
                                    type="checkbox"
                                    className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                    checked={selectedListings.includes(
                                      apartment,
                                    )}
                                    onChange={() =>
                                      handleApartmentSelection(apartment)
                                    }
                                  />
                                  <div>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleOpenTenantsModal(apartment)
                                      }
                                      className={`text-left font-medium ${
                                        selectedListings.includes(apartment)
                                          ? "text-blue-900"
                                          : "text-gray-900"
                                      } hover:underline`}
                                    >
                                      {apartment.title}
                                    </button>
                                    <div className="flex flex-wrap items-center gap-2 mt-1">
                                      <div className="flex items-center gap-1">
                                        <BedIcon className="h-3.5 w-3.5 text-gray-400" />
                                        <span className="text-xs text-gray-500">
                                          {apartment.numOfBedrooms} br
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <ApartmentIcon className="h-3.5 w-3.5 text-gray-400" />
                                        <span className="text-xs text-gray-500">
                                          {apartment.building}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <button
                                  onClick={() =>
                                    setMobileMenuApartment(apartment)
                                  }
                                  className="-m-2 p-2 text-gray-500 hover:text-gray-900"
                                >
                                  <EllipsisVerticalIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                  />
                                </button>
                              </div>

                              <div className="mt-3 border-t pt-3 space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <div className="text-xs text-gray-500 font-medium">
                                      Rental Price
                                    </div>
                                    <div className="flex items-center gap-1 mt-1">
                                      <AttachMoneyIcon className="h-3.5 w-3.5 text-gray-400" />
                                      <span className="text-sm font-medium text-gray-900">
                                        ${apartment.rent ?? 0}
                                      </span>
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500 font-medium">
                                      Current Rent
                                    </div>
                                    <div className="flex items-center gap-1 mt-1">
                                      <AttachMoneyIcon className="h-3.5 w-3.5 text-gray-400" />
                                      <span className="text-sm font-medium text-gray-900">
                                        ${apartment.currentRent ?? 0}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <div className="text-xs text-gray-500 font-medium">
                                      Location
                                    </div>
                                    <div className="flex items-center gap-1 mt-1">
                                      <RoomIcon className="h-3.5 w-3.5 text-gray-400" />
                                      <span className="text-xs text-gray-700">
                                        Line {apartment.line}, Floor{" "}
                                        {apartment.floor}
                                      </span>
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500 font-medium">
                                      Move-Out
                                    </div>
                                    <div className="text-sm text-gray-900 mt-1">
                                      {apartment.moveOutDate
                                        ? new Date(
                                            apartment.moveOutDate,
                                          ).toLocaleDateString()
                                        : "N/A"}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="mt-3 flex gap-2">
                                <button
                                  onClick={() =>
                                    handleMoveOutReminder(apartment)
                                  }
                                  className="flex-1 inline-flex items-center justify-center gap-1 py-1.5 px-2 text-xs rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                                >
                                  <BellIcon className="h-3.5 w-3.5" />
                                  Reminder
                                </button>
                                <button
                                  onClick={() => handleMakeMoveOut(apartment)}
                                  className="flex-1 inline-flex items-center justify-center gap-1 py-1.5 px-2 text-xs rounded-md bg-green-50 text-green-700 hover:bg-green-100"
                                >
                                  <CheckBadgeIcon className="h-3.5 w-3.5" />
                                  Send Deposit Mail
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      /* Desktop Table View */
                      <div className="border rounded-md overflow-hidden shadow">
                        <table className="min-w-full table-fixed divide-y divide-gray-300">
                          <thead className="text-center border-0 rounded-t-md overflow-hidden">
                            <tr>
                              <th
                                scope="col"
                                className="relative px-4 sm:px-6 sm:w-12"
                              >
                                <input
                                  type="checkbox"
                                  className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                  ref={checkbox}
                                  checked={checked}
                                  onChange={toggleAll}
                                />
                              </th>

                              <th
                                scope="col"
                                className="py-3 px-3 text-left text-sm font-semibold text-black"
                              >
                                Title
                              </th>

                              <th
                                scope="col"
                                className="py-3 px-3 text-left text-sm font-semibold text-black"
                              >
                                Building
                              </th>

                              <th
                                scope="col"
                                className="py-3 px-3 text-left text-sm font-semibold text-black"
                              >
                                Pricing
                              </th>

                              <th
                                scope="col"
                                className="py-3 px-3 text-left text-sm font-semibold text-black"
                              >
                                Move-Out Date
                              </th>

                              <th
                                scope="col"
                                className="relative py-3 pl-3 pr-4 sm:pr-3"
                              >
                                <span className="sr-only">Edit</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody
                            className={
                              "divide-y divide-gray-200 bg-white " +
                              (loading && "relative")
                            }
                          >
                            {filteredListings.length > 0 &&
                              filteredListings.map((apartment) => (
                                <tr
                                  key={apartment.id}
                                  className={`border-b ${
                                    selectedListings.includes(apartment)
                                      ? "bg-gray-50"
                                      : ""
                                  }`}
                                >
                                  <td className="relative px-4 sm:px-6 sm:w-12">
                                    {selectedListings.includes(apartment) && (
                                      <div className="absolute inset-y-0 left-0 w-0.5 bg-indigo-600" />
                                    )}
                                    <input
                                      type="checkbox"
                                      className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                      checked={selectedListings.includes(
                                        apartment,
                                      )}
                                      onChange={() =>
                                        handleApartmentSelection(apartment)
                                      }
                                    />
                                  </td>

                                  <td className="px-3 py-4">
                                    <div className="flex flex-col">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleOpenTenantsModal(apartment)
                                        }
                                        className={`text-left font-medium ${
                                          selectedListings.includes(apartment)
                                            ? "text-blue-900"
                                            : "text-gray-900"
                                        } hover:underline`}
                                      >
                                        {apartment.title}
                                      </button>
                                      <div className="flex items-center gap-2 mt-1">
                                        <BedIcon
                                          style={{ fontSize: "15px" }}
                                          className="text-gray-400"
                                        />
                                        <span className="text-xs text-gray-500">
                                          {apartment.numOfBedrooms} bedroom
                                          {apartment.numOfBedrooms !== 1
                                            ? "s"
                                            : ""}
                                        </span>
                                      </div>
                                    </div>
                                  </td>

                                  <td className="px-3 py-4">
                                    <div className="flex flex-col gap-1">
                                      <div className="flex items-center gap-2">
                                        <ApartmentIcon
                                          style={{ fontSize: "15px" }}
                                          className="text-gray-400"
                                        />
                                        <span className="text-sm text-gray-900">
                                          {apartment.building}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <RoomIcon
                                          style={{ fontSize: "15px" }}
                                          className="text-gray-400"
                                        />
                                        <span className="text-xs text-gray-500">
                                          Line {apartment.line}, Floor{" "}
                                          {apartment.floor}
                                        </span>
                                      </div>
                                    </div>
                                  </td>

                                  <td className="px-3 py-4">
                                    <div className="flex flex-col gap-1">
                                      <div className="flex items-center">
                                        <span className="text-sm font-medium text-gray-900 mr-2">
                                          $ {apartment.rent ?? 0}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          Rental
                                        </span>
                                      </div>
                                      <div className="flex items-center">
                                        <span className="text-sm text-gray-600 mr-2">
                                          $ {apartment.currentRent ?? 0}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          Current
                                        </span>
                                      </div>
                                    </div>
                                  </td>

                                  <td className="px-3 py-4 text-sm text-gray-500">
                                    {apartment.moveOutDate
                                      ? new Date(
                                          apartment.moveOutDate,
                                        ).toLocaleDateString()
                                      : ""}
                                  </td>

                                  <td>
                                    <Menu as="div" className="flex-none">
                                      <Menu.Button className="-m-2 p-2 text-gray-500 hover:text-gray-900 flex items-center justify-center">
                                        <span className="sr-only">
                                          Open options
                                        </span>
                                        <EllipsisVerticalIcon
                                          className="h-5 w-5"
                                          aria-hidden="true"
                                        />
                                      </Menu.Button>
                                      <Transition
                                        as={Fragment}
                                        enter="transition ease-out duration-100"
                                        enterFrom="transform opacity-0 scale-95"
                                        enterTo="transform opacity-100 scale-100"
                                        leave="transition ease-in duration-75"
                                        leaveFrom="transform opacity-100 scale-100"
                                        leaveTo="transform opacity-0 scale-95"
                                      >
                                        <Menu.Items className="absolute right-2 z-10 mt-2 origin-bottom-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none min-w-[200px]">
                                          <Menu.Item>
                                            {({ active }) => (
                                              <button
                                                onClick={() =>
                                                  handleAddEditListing(
                                                    apartment,
                                                  )
                                                }
                                                className={classNames(
                                                  active ? "bg-gray-50" : "",
                                                  "flex w-full px-3 py-2 items-center text-sm leading-6 text-gray-900",
                                                )}
                                              >
                                                <PencilIcon className="h-5 w-5 mr-2" />
                                                Edit Apartment
                                              </button>
                                            )}
                                          </Menu.Item>

                                          <Menu.Item>
                                            {({ active }) => (
                                              <button
                                                onClick={() =>
                                                  navigate(
                                                    `/timeline/by-listing/${apartment.id}`,
                                                  )
                                                }
                                                className={classNames(
                                                  active ? "bg-gray-50" : "",
                                                  "flex w-full px-3 py-2 items-center text-sm leading-6 text-gray-900",
                                                )}
                                              >
                                                <ClockIcon className="h-5 w-5 mr-2" />
                                                View Timeline
                                              </button>
                                            )}
                                          </Menu.Item>

                                          <Menu.Item>
                                            {({ active }) => (
                                              <button
                                                onClick={() =>
                                                  handleAddAppointment(
                                                    apartment,
                                                  )
                                                }
                                                className={classNames(
                                                  active ? "bg-gray-50" : "",
                                                  "flex w-full px-3 py-2 items-center text-sm leading-6 text-gray-900",
                                                )}
                                              >
                                                <PlusIcon className="h-5 w-5 mr-2" />
                                                Add Appointment
                                              </button>
                                            )}
                                          </Menu.Item>

                                          <Menu.Item>
                                            {({ active }) => (
                                              <button
                                                onClick={() =>
                                                  handleShowAvailableAppointments(
                                                    apartment,
                                                  )
                                                }
                                                className={classNames(
                                                  active ? "bg-gray-50" : "",
                                                  "flex w-full px-3 py-2 items-center text-sm leading-6 text-gray-900",
                                                )}
                                              >
                                                <CalendarDaysIcon className="h-5 w-5 mr-2" />
                                                Show Available Appointments
                                              </button>
                                            )}
                                          </Menu.Item>

                                          <Menu.Item>
                                            {({ active }) => (
                                              <button
                                                onClick={() =>
                                                  listed
                                                    ? handleDeleteApartment(
                                                        apartment,
                                                      )
                                                    : handleListApartment(
                                                        apartment,
                                                      )
                                                }
                                                className={classNames(
                                                  active ? "bg-gray-50" : "",
                                                  listed ? "" : "hidden",
                                                  "flex w-full px-3 py-2 items-center text-sm leading-6 text-gray-900",
                                                )}
                                              >
                                                <MinusCircleIcon className="h-5 w-5 mr-2" />
                                                Unlist Apartment
                                              </button>
                                            )}
                                          </Menu.Item>

                                          <Menu.Item>
                                            {({ active }) => (
                                              <button
                                                onClick={() =>
                                                  handleListApartment(apartment)
                                                }
                                                className={classNames(
                                                  active ? "bg-gray-50" : "",
                                                  listed ? "hidden" : "",
                                                  "flex w-full px-3 py-2 items-center text-sm leading-6 text-gray-900",
                                                )}
                                              >
                                                <CheckCircleIcon className="h-5 w-5 mr-2" />
                                                List Apartment
                                              </button>
                                            )}
                                          </Menu.Item>

                                          <Menu.Item>
                                            {({ active }) => (
                                              <button
                                                onClick={() =>
                                                  handleAutomaticEmail(
                                                    apartment,
                                                  )
                                                }
                                                className={classNames(
                                                  active ? "bg-gray-50" : "",
                                                  "flex w-full px-3 py-2 items-center text-sm leading-6 text-gray-900",
                                                )}
                                              >
                                                {apartment.automaticEmail ? (
                                                  <PauseCircle className="h-5 w-5 mr-2" />
                                                ) : (
                                                  <PlayCircle className="h-5 w-5 mr-2" />
                                                )}
                                                {apartment.automaticEmail
                                                  ? "Stop Automatic Email"
                                                  : "Start Automatic Email"}
                                              </button>
                                            )}
                                          </Menu.Item>

                                          <Menu.Item>
                                            {({ active }) => (
                                              <a
                                                href={`/emails/${apartment.id}`}
                                                className={classNames(
                                                  active ? "bg-gray-50" : "",
                                                  "flex px-3 py-2 items-center text-sm leading-6 text-gray-900",
                                                )}
                                              >
                                                <EmailIcon className="h-4 w-4 mr-2" />
                                                Emails
                                              </a>
                                            )}
                                          </Menu.Item>

                                          <Menu.Item>
                                            {({ active }) => (
                                              <button
                                                onClick={() =>
                                                  handleMoveOutReminder(
                                                    apartment,
                                                  )
                                                }
                                                className={classNames(
                                                  active ? "bg-gray-50" : "",
                                                  "flex w-full px-3 py-2 items-center text-sm leading-6 text-gray-900",
                                                )}
                                              >
                                                <BellIcon className="h-5 w-5 mr-2" />
                                                Set Move Out Reminder
                                              </button>
                                            )}
                                          </Menu.Item>

                                          <Menu.Item>
                                            {({ active }) => (
                                              <button
                                                onClick={() =>
                                                  handleSendGeneralInstructionEmail(
                                                    apartment,
                                                  )
                                                }
                                                className={classNames(
                                                  active ? "bg-gray-50" : "",
                                                  "flex w-full px-3 py-2 items-center text-sm leading-6 text-gray-900",
                                                )}
                                              >
                                                <EnvelopeIcon className="h-5 w-5 mr-2" />
                                                Send General Instruction Mail
                                              </button>
                                            )}
                                          </Menu.Item>

                                          <Menu.Item>
                                            {({ active }) => (
                                              <button
                                                onClick={() =>
                                                  handleMakeMoveOut(apartment)
                                                }
                                                className={classNames(
                                                  active ? "bg-gray-50" : "",
                                                  "flex w-full px-3 py-2 items-center text-sm leading-6 text-gray-900",
                                                )}
                                              >
                                                <EnvelopeIcon className="h-5 w-5 mr-2" />
                                                Send Security Deposit Mail
                                              </button>
                                            )}
                                          </Menu.Item>

                                          <Menu.Item>
                                            {({ active }) => (
                                              <button
                                                onClick={() =>
                                                  handleSendSlotBookingMail(
                                                    apartment,
                                                  )
                                                }
                                                className={classNames(
                                                  active ? "bg-gray-50" : "",
                                                  "flex w-full px-3 py-2 items-center text-sm leading-6 text-gray-900",
                                                )}
                                              >
                                                <EnvelopeIcon className="h-5 w-5 mr-2" />
                                                Send Slot Booking Mail
                                              </button>
                                            )}
                                          </Menu.Item>
                                        </Menu.Items>
                                      </Transition>
                                    </Menu>
                                  </td>
                                </tr>
                              ))}

                            {loading && (
                              <>
                                {filteredListings.length === 0 && (
                                  <tr className="invisible h-12"></tr>
                                )}
                                <tr className="absolute bg-gray-300/80 top-0 left-0 p-4 w-full min-h-12 h-full flex items-center justify-center">
                                  <Loading />
                                </tr>
                              </>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {filteredListings.length === 0 && !loading && (
                      <div className="text-gray-400 text-sm w-full border rounded-b-md shadow flex items-center justify-center p-4 sm:p-8">
                        No Apartments Found
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Menu Modal */}
            {mobileMenuApartment && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-sm max-h-[90vh] overflow-y-auto">
                  <div className="flex justify-between items-center p-4 border-b">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Options
                    </h3>
                    <button
                      onClick={() => setMobileMenuApartment(null)}
                      className="text-gray-500 hover:text-gray-800 p-1"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="p-4 space-y-2">
                    <button
                      onClick={() => {
                        handleAddEditListing(mobileMenuApartment);
                        setMobileMenuApartment(null);
                      }}
                      className="flex w-full items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                    >
                      <PencilIcon className="h-5 w-5 mr-3" />
                      Edit Apartment
                    </button>
                    <button
                      onClick={() => {
                        navigate(`/timeline/by-listing/${mobileMenuApartment.id}`);
                        setMobileMenuApartment(null);
                      }}
                      className="flex w-full items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                    >
                      <ClockIcon className="h-5 w-5 mr-3" />
                      View Timeline
                    </button>
                    <button
                      onClick={() => {
                        handleAddAppointment(mobileMenuApartment);
                        setMobileMenuApartment(null);
                      }}
                      className="flex w-full items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                    >
                      <PlusIcon className="h-5 w-5 mr-3" />
                      Add Appointment
                    </button>
                    <button
                      onClick={() => {
                        handleShowAvailableAppointments(mobileMenuApartment);
                        setMobileMenuApartment(null);
                      }}
                      className="flex w-full items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                    >
                      <CalendarDaysIcon className="h-5 w-5 mr-3" />
                      Show Available Appointments
                    </button>
                    <button
                      onClick={() => {
                        listed
                          ? handleDeleteApartment(mobileMenuApartment)
                          : handleListApartment(mobileMenuApartment);
                        setMobileMenuApartment(null);
                      }}
                      className="flex w-full items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                    >
                      {listed ? (
                        <>
                          <MinusCircleIcon className="h-5 w-5 mr-3" />
                          Unlist Apartment
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="h-5 w-5 mr-3" />
                          List Apartment
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        handleAutomaticEmail(mobileMenuApartment);
                        setMobileMenuApartment(null);
                      }}
                      className="flex w-full items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                    >
                      {mobileMenuApartment.automaticEmail ? (
                        <PauseIcon className="h-5 w-5 mr-3" />
                      ) : (
                        <PlayIcon className="h-5 w-5 mr-3" />
                      )}
                      {mobileMenuApartment.automaticEmail
                        ? "Stop Auto Email"
                        : "Start Auto Email"}
                    </button>
                    <a
                      href={`/emails/${mobileMenuApartment.id}`}
                      onClick={() => setMobileMenuApartment(null)}
                      className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                    >
                      <EnvelopeIcon className="h-5 w-5 mr-3" />
                      Emails
                    </a>
                    <button
                      onClick={() => {
                        handleMoveOutReminder(mobileMenuApartment);
                        setMobileMenuApartment(null);
                      }}
                      className="flex w-full items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                    >
                      <BellIcon className="h-5 w-5 mr-3" />
                      Set Move Out Reminder
                    </button>
                    <button
                      onClick={() => {
                        handleSendGeneralInstructionEmail(mobileMenuApartment);
                        setMobileMenuApartment(null);
                      }}
                      className="flex w-full items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                    >
                      <EnvelopeIcon className="h-5 w-5 mr-3" />
                      Send General Instruction Mail
                    </button>
                    <button
                      onClick={() => {
                        handleMakeMoveOut(mobileMenuApartment);
                        setMobileMenuApartment(null);
                      }}
                      className="flex w-full items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                    >
                      <CheckBadgeIcon className="h-5 w-5 mr-3" />
                      Send Security Deposit Receiver Mail
                    </button>
                    <button
                      onClick={() => {
                        handleSendSlotBookingMail(mobileMenuApartment);
                        setMobileMenuApartment(null);
                      }}
                      className="flex w-full items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                    >
                      <EnvelopeIcon className="h-5 w-5 mr-3" />
                      Send Slot Booking Mail to Tenant
                    </button>
                  </div>
                  <div className="p-4 border-t">
                    <button
                      onClick={() => setMobileMenuApartment(null)}
                      className="w-full px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Tenants Modal */}
            {openTenantsModal && selectedListingForTenants && (
              <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/20 p-3 sm:p-4 pt-20 sm:pt-24">
                <div className="bg-white rounded w-full max-w-xl max-h-[80vh] sm:max-h-[75vh] flex flex-col overflow-hidden mx-auto shadow">
                  {/* Header */}
                  <div className="border-b bg-white px-4 py-3 sm:px-5 sm:py-4 flex items-center justify-between flex-shrink-0">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                      Tenants & Guarantors
                    </h2>
                    <button
                      type="button"
                      onClick={handleCloseTenantsModal}
                      className="text-gray-400 hover:text-gray-600 transition flex-shrink-0"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Body */}
                  <div className="flex-1 overflow-y-auto px-4 py-3 sm:px-5 sm:py-4 space-y-3">
                    {tenantError && (
                      <div className="rounded bg-gray-100 border border-gray-300 p-3 text-xs sm:text-sm text-gray-800 flex-shrink-0">
                        <p className="font-medium">Error</p>
                        <p className="mt-1">{tenantError}</p>
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={handleStartAddTenant}
                        className="flex-1 py-2 px-3 text-xs sm:text-sm font-medium rounded bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
                      >
                        Add Tenant
                      </button>
                      {tenants.length > 0 && (
                        <button
                          type="button"
                          onClick={handleOpenReceiverModal}
                          className="flex-1 py-2 px-3 text-xs sm:text-sm font-medium rounded bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
                        >
                          Select Security Deposit Receiver
                        </button>
                      )}
                    </div>

                    {tenantLoading ? (
                      <div className="flex justify-center py-6 sm:py-8">
                        <Loading />
                      </div>
                    ) : tenants.length === 0 ? (
                      <div className="text-center py-6 text-gray-500 text-sm">
                        No tenants added
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {tenants.map((tenant) => (
                          <div
                            key={tenant.id}
                            className="border border-gray-200 rounded p-3 sm:p-4 bg-gray-50"
                          >
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-gray-900 text-xs sm:text-sm">
                                  {tenant.name}
                                </h3>
                                <p className="text-xs text-gray-600 break-all mt-0.5">
                                  {tenant.email}
                                </p>
                                <p className="text-xs text-gray-600">
                                  {tenant.phone}
                                </p>
                              </div>
                              <div className="flex gap-1 flex-shrink-0 text-xs">
                                <button
                                  type="button"
                                  onClick={() => handleStartEditTenant(tenant)}
                                  className="px-2 py-1 font-medium text-gray-700 border border-gray-300 rounded hover:bg-gray-100 transition"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTenantToDelete(tenant);
                                    setOpenDeleteTenantConfirm(true);
                                  }}
                                  className="px-2 py-1 font-medium text-gray-700 border border-gray-300 rounded hover:bg-gray-100 transition"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {tenant.isGuarantor && (
                                <span className="inline-block bg-gray-200 text-gray-800 text-xs px-2 py-0.5 rounded">
                                  Guarantor
                                </span>
                              )}
                              {tenant.isReceiver && (
                                <span className="inline-block bg-gray-200 text-gray-800 text-xs px-2 py-0.5 rounded">
                                  Receiver
                                </span>
                              )}
                              {tenant.showingAppointmentStarted && (
                                <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded">
                                  Showing Started
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {showTenantForm && (
                      <div className="mt-3 pt-3 border-t space-y-2.5">
                        <h3 className="text-sm font-semibold text-gray-900">
                          {isEditingTenant ? "Edit Tenant" : "Add Tenant"}
                        </h3>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-0.5">
                            Name
                          </label>
                          <input
                            type="text"
                            value={tenantForm.name}
                            onChange={(e) =>
                              setTenantForm((f) => ({
                                ...f,
                                name: e.target.value,
                              }))
                            }
                            className="block w-full rounded border border-gray-300 text-xs sm:text-sm px-2.5 py-1.5 focus:border-gray-500 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-0.5">
                            Email
                          </label>
                          <input
                            type="email"
                            value={tenantForm.email}
                            onChange={(e) =>
                              setTenantForm((f) => ({
                                ...f,
                                email: e.target.value,
                              }))
                            }
                            className="block w-full rounded border border-gray-300 text-xs sm:text-sm px-2.5 py-1.5 focus:border-gray-500 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-0.5">
                            Phone
                          </label>
                          <input
                            type="text"
                            value={tenantForm.phone}
                            onChange={(e) =>
                              setTenantForm((f) => ({
                                ...f,
                                phone: e.target.value,
                              }))
                            }
                            className="block w-full rounded border border-gray-300 text-xs sm:text-sm px-2.5 py-1.5 focus:border-gray-500 focus:outline-none"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="flex items-center gap-2 text-xs text-gray-700">
                            <input
                              type="checkbox"
                              checked={tenantForm.isGuarantor}
                              onChange={(e) =>
                                setTenantForm((f) => ({
                                  ...f,
                                  isGuarantor: e.target.checked,
                                }))
                              }
                              className="rounded border-gray-300"
                            />
                            Guarantor
                          </label>
                          <label className="flex items-center gap-2 text-xs text-gray-700">
                            <input
                              type="checkbox"
                              checked={tenantForm.isReceiver}
                              onChange={(e) =>
                                setTenantForm((f) => ({
                                  ...f,
                                  isReceiver: e.target.checked,
                                }))
                              }
                              className="rounded border-gray-300"
                            />
                            Receiver
                          </label>
                        </div>
                        <div className="flex gap-2 pt-1">
                          <button
                            type="button"
                            onClick={() => setShowTenantForm(false)}
                            className="flex-1 px-2 py-1.5 text-xs sm:text-sm font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleSaveTenant}
                            className="flex-1 px-2 py-1.5 text-xs sm:text-sm font-medium rounded bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="border-t bg-gray-50 px-4 py-3 sm:px-5 sm:py-3 flex justify-end flex-shrink-0">
                    <button
                      type="button"
                      onClick={handleCloseTenantsModal}
                      className="px-3 py-1.5 text-xs sm:text-sm font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            <SecurityDepositReceiverModal
              open={openReceiverModal}
              onClose={handleCloseReceiverModal}
              listingId={selectedListingForTenants?.id || ""}
              tenants={tenants}
              loading={tenantLoading}
              onReceiverUpdated={handleReceiverUpdated}
            />

            <AddAppointment
              open={openAddAppointment}
              setOpen={setOpenAddAppointment}
              isVacant={
                selectedListingsToAddAppointment?.isVacant !== undefined
                  ? selectedListingsToAddAppointment.isVacant
                  : false
              }
              listingId={selectedListingsToAddAppointment?.id ?? ""}
              appointmentToBeEdited={emptyAppointment}
              loadMainAppointment={false}
            />
            <DefineSlotOrder
              open={showDefineSlotOrder}
              setOpen={setShowDefineSlotOrder}
              availableAppointments={emptyAppointments}
            />
            <AddRentedListing
              open={showAddListing}
              setOpen={setShowAddListing}
              editListings={selectedListings}
              setEditListings={setSelectedListings}
              refreshApartments={refreshApartments}
              onModalClose={handleModalClose}
            />
            <MessageBox
              title={"Unlist Apartment"}
              message={
                "Are you sure you want to unlist the selected apartment(s) along with their associated information?"
              }
              isOpen={openMessageBox}
              setIsOpen={setOpenMessageBox}
              okButtonText="Ok"
              cancelButtonText="Cancel"
              okButtonFunction={okButtonFunction}
              cancelButtonFunction={cancelButtonFunction}
              okButtonStyle={{
                backgroundColor: "rgb(79, 70, 229)",
              }}
              iconColor={{ color: "rgb(79, 70, 229)" }}
            />
            <MessageBox
              title={"Delete Confirmation"}
              message={
                tenantToDelete
                  ? `Are you sure you want to delete ${tenantToDelete.name}?`
                  : "Are you sure you want to delete this tenant?"
              }
              isOpen={openDeleteTenantConfirm}
              setIsOpen={setOpenDeleteTenantConfirm}
              zIndex="z-50"
              okButtonText="Delete"
              cancelButtonText="Cancel"
              okButtonFunction={handleConfirmDeleteTenant}
              cancelButtonFunction={() => setOpenDeleteTenantConfirm(false)}
              okButtonStyle={{ backgroundColor: "#ff2c2c" }}
              iconColor={{ color: "#ff2c2c" }}
            />
            <MessageBox
              title={"Unlist Apartment"}
              message={
                "This apartment has some available appointments. Are you sure you want to delete this?"
              }
              isOpen={openMessageBox2}
              setIsOpen={setOpenMessageBox2}
              okButtonText="Ok"
              cancelButtonText="Cancel"
              okButtonFunction={okButtonFunction2}
              cancelButtonFunction={() => setOpenMessageBox2(false)}
              okButtonStyle={{ backgroundColor: "#ff2c2c" }}
              iconColor={{ color: "#ff2c2c" }}
            />

            <MoveOutReminderModal
              open={showMoveOutReminderModal}
              setOpen={setShowMoveOutReminderModal}
              listingId={selectedListingForReminder?.id || ""}
              listingTitle={selectedListingForReminder?.title || ""}
            />
          </div>
        )}
      </div>
    </>
  );
}
